import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { KeyRound, Mail, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { defaultPathForRole } from '@/lib/auth'
import { useAuth } from '@/lib/auth'
import { ApiError } from '@/lib/api'

interface LocationState {
  from?: { pathname?: string }
}

export default function Login() {
  const { login, verifyMfa, user, status } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState | null
  const redirectTo = state?.from?.pathname

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaError, setMfaError] = useState<string | null>(null)
  const [mfaSubmitting, setMfaSubmitting] = useState(false)

  // Si ya está autenticado, redirigir al layout.
  useEffect(() => {
    if (status === 'authenticated' && user) {
      navigate(redirectTo ?? defaultPathForRole(user.role), { replace: true })
    }
  }, [status, user, navigate, redirectTo])

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setFormError(null)
    setSubmitting(true)
    try {
      const res = await login(email.trim(), password)
      if (res.requiresMfa && res.mfaChallenge) {
        setMfaChallenge(res.mfaChallenge)
      }
      // Si no requiere MFA, el useEffect redirige automáticamente al authenticar.
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 401
          ? 'Credenciales inválidas'
          : err instanceof ApiError
            ? 'No se pudo iniciar sesión'
            : 'Error de conexión con el servidor'
      setFormError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const onMfa = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!mfaChallenge) return
    setMfaError(null)
    setMfaSubmitting(true)
    try {
      await verifyMfa(mfaChallenge, mfaCode.trim())
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 401
          ? 'Código inválido'
          : 'No se pudo verificar el código'
      setMfaError(message)
    } finally {
      setMfaSubmitting(false)
    }
  }

  return (
    <main className="flex min-h-dvh flex-col bg-slate-50">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-4 py-10">
        <div className="mb-8 flex w-full flex-col items-start gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-fitlab-gradient px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-sm">
            <span className="size-1.5 rounded-full bg-white/90" aria-hidden />
            Salud Ocupacional
          </span>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            <span className="bg-fitlab-gradient bg-clip-text text-transparent">FITLAB</span>
          </h1>
          <p className="text-sm text-slate-600">
            Ingresa con tu cuenta corporativa para acceder al sistema.
          </p>
        </div>

        <div className="w-full rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          {!mfaChallenge ? (
            <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
              <h2 className="text-lg font-semibold text-slate-900">Iniciar sesión</h2>

              <Input
                label="Correo electrónico"
                type="email"
                autoComplete="email"
                required
                leftIcon={<Mail className="h-4 w-4" />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@fitlab.cl"
              />
              <Input
                label="Contraseña"
                type="password"
                autoComplete="current-password"
                required
                leftIcon={<KeyRound className="h-4 w-4" />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              {formError && (
                <div
                  role="alert"
                  className="rounded-md border border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5 px-3 py-2 text-sm text-[color:var(--color-fitlab-red-hover)]"
                >
                  {formError}
                </div>
              )}

              <Button type="submit" loading={submitting} className="mt-2 w-full" size="lg">
                Iniciar sesión
              </Button>
            </form>
          ) : (
            <form onSubmit={onMfa} className="flex flex-col gap-4" noValidate>
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-fitlab-blue)]/10">
                  <ShieldCheck
                    className="h-5 w-5 text-[color:var(--color-fitlab-blue)]"
                    aria-hidden
                  />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Verificación en dos pasos
                  </h2>
                  <p className="text-sm text-slate-600">
                    Ingresa el código de 6 dígitos de tu app de autenticación.
                  </p>
                </div>
              </div>

              <Input
                label="Código de verificación MFA"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
              />

              {mfaError && (
                <div
                  role="alert"
                  className="rounded-md border border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5 px-3 py-2 text-sm text-[color:var(--color-fitlab-red-hover)]"
                >
                  {mfaError}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setMfaChallenge(null)
                    setMfaCode('')
                    setMfaError(null)
                  }}
                >
                  Volver
                </Button>
                <Button type="submit" loading={mfaSubmitting} className="flex-1" size="lg">
                  Verificar
                </Button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          Acceso restringido. Contenido sujeto a acuerdo de confidencialidad.
        </p>
      </div>
    </main>
  )
}
